import { Controller } from "@hotwired/stimulus"
import SCRIPTS from "./home_demo_scripts"

// Homepage product demos. A fake cursor clicks through a static replica of the
// app (shared/home_demo/*); which script it runs comes from the script value.
// Anything the cursor can reach carries data-t; view state lives in data
// attributes that scripts flip and CSS styles. Loops while on screen, stops
// when scrolled away, skipped for reduced motion. Each loop restores the
// screen from an untouched <template> copy, so a Turbo snapshot taken
// mid-demo can't become the new starting state.
class Cancelled extends Error {}

const ease = (t) => (t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2)

export default class extends Controller {
  static targets = ["screen", "cursor", "template"]
  static values = { script: String }

  connect() {
    if (matchMedia("(prefers-reduced-motion: reduce)").matches) return
    this.pos = { x: 0, y: 0 }
    this.observer = new IntersectionObserver(
      ([entry]) => (entry.isIntersecting ? this.start() : this.stop()),
      { threshold: 0.3 }
    )
    this.observer.observe(this.element)
  }

  disconnect() {
    this.stop()
    this.observer?.disconnect()
  }

  start() {
    if (this.run) return
    this.run = {}
    this.frame = requestAnimationFrame(this.tick)
    ;(async () => {
      try {
        for (;;) {
          this.reset()
          await this.wait(500)
          await SCRIPTS[this.scriptValue](this)
        }
      } catch (e) {
        if (!(e instanceof Cancelled)) throw e
      }
    })()
  }

  stop() {
    this.run = null
    cancelAnimationFrame(this.frame)
    this.hovered?.removeAttribute("data-hover")
    this.hovered = null
  }

  reset() {
    this.screenTarget.replaceChildren(this.templateTarget.content.cloneNode(true))
    for (const key of Object.keys(this.screenTarget.dataset)) {
      if (key !== "homeDemoTarget") delete this.screenTarget.dataset[key]
    }
    this.hovered = null
    this.motion = null
    const box = this.element.getBoundingClientRect()
    this.pos = { x: box.width * 0.82, y: box.height + 30 }
    this.place()
  }

  // --- what scripts call ----------------------------------------------------

  find(selector) {
    return this.screenTarget.querySelector(selector)
  }

  findAll(selector) {
    return this.screenTarget.querySelectorAll(selector)
  }

  // Screen-level state, e.g. set("view", "widgets") → data-view="widgets".
  set(key, value) {
    this.screenTarget.dataset[key] = value
  }

  url(text) {
    this.find("[data-url]").textContent = text
  }

  nav(tab) {
    this.find("[data-nav-active]").dataset.navActive = tab
  }

  wait(ms) {
    const run = this.run
    return new Promise((resolve, reject) =>
      setTimeout(() => (run && this.run === run ? resolve() : reject(new Cancelled())), ms)
    )
  }

  async clickOn(name, offset) {
    await this.move(name, offset)
    await this.click()
  }

  // Curved, eased path to the centre of [data-t=name]; bows to a random side
  // and takes longer for longer trips, which is most of what makes it feel human.
  async move(name, { dx = 0, dy = 0 } = {}) {
    const el = this.find(`[data-t="${name}"]`)
    if (!el) return
    const box = this.element.getBoundingClientRect()
    const r = el.getBoundingClientRect()
    const from = { ...this.pos }
    const to = { x: r.left - box.left + r.width / 2 + dx, y: r.top - box.top + r.height / 2 + dy }
    const ox = to.x - from.x
    const oy = to.y - from.y
    const dist = Math.hypot(ox, oy) || 1
    const bow = Math.min(60, dist * 0.12) * (Math.random() < 0.5 ? -1 : 1)
    const ctrl = { x: (from.x + to.x) / 2 - (oy / dist) * bow, y: (from.y + to.y) / 2 + (ox / dist) * bow }
    const ms = Math.round(Math.min(950, 280 + dist * 0.75))
    this.motion = { from, to, ctrl, ms, start: performance.now() }
    await this.wait(ms + 40)
  }

  async click() {
    const ring = document.createElement("span")
    ring.className = "hd-click"
    ring.addEventListener("animationend", () => ring.remove())
    this.cursorTarget.append(ring)
    this.cursorTarget.classList.add("is-down")
    await this.wait(110)
    this.cursorTarget.classList.remove("is-down")
    await this.wait(90)
  }

  // Types into a .hd-field, one character at a time with a little jitter.
  async type(selector, text, { ms = 55 } = {}) {
    const field = this.find(selector)
    field.dataset.typing = ""
    for (let i = 1; i <= text.length; i++) {
      field.textContent = text.slice(0, i)
      await this.wait(ms + Math.random() * 40)
    }
    delete field.dataset.typing
  }

  // --- cursor ---------------------------------------------------------------

  tick = (now) => {
    const m = this.motion
    if (m) {
      const t = Math.min(1, (now - m.start) / m.ms)
      const s = ease(t)
      const u = 1 - s
      this.pos.x = u * u * m.from.x + 2 * u * s * m.ctrl.x + s * s * m.to.x
      this.pos.y = u * u * m.from.y + 2 * u * s * m.ctrl.y + s * s * m.to.y
      if (t >= 1) this.motion = null
      this.place()
    }
    this.hover()
    this.frame = requestAnimationFrame(this.tick)
  }

  place() {
    this.cursorTarget.style.transform = `translate(${this.pos.x}px, ${this.pos.y}px)`
  }

  // Fake hover: whatever [data-t] sits under the cursor gets data-hover, and
  // the cursor turns into a hand over it.
  hover() {
    const box = this.element.getBoundingClientRect()
    const under = document.elementFromPoint(box.left + this.pos.x, box.top + this.pos.y)
    const target = under && this.screenTarget.contains(under) ? under.closest("[data-t]") : null
    if (target === this.hovered) return
    this.hovered?.removeAttribute("data-hover")
    target?.setAttribute("data-hover", "")
    this.hovered = target
    this.cursorTarget.classList.toggle("is-pointer", !!target)
  }
}
